import {observer} from 'mobx-react-lite'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {DataObjectId, DeadlineStickerEntry, Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {IconButtonNew} from '@components/common/base/buttons'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {DropdownContent} from '@components/common/dropdown/dropdown-content'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {
  BlockedPointText,
  EmptyBlockedDeadlineHint,
  formatDeadline
} from '@components/sticker/sticker-menu/sticker-menu-deadline/utils'
import {
  useTimelineConfig,
  useTimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {ColumnId, DeadlineState} from '@components/timeline-new/types'
import {
  useForceUpdate,
  useHover,
  useSyncEvent,
  useSyncEventUpdateEffect,
  useTooltipVisible
} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {GanttDraft} from '@extensions/gantt-draft'
import {globals} from '@extensions/globals-base'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {Sticker} from '@extensions/sticker/sticker'
import {
  DeadlineEditStatefulForm,
  getDefaultEntry,
  getValue
} from '@extensions/sticker/views-web/deadline/deadline-edit'
import {TwCell, TwTooltipText} from './containers'
import {ColumnProps} from './types'
import {useSnappedDeadline} from './use-snapped-deadline'

// Add delta to start and remove from end to ensure no local time diff affects dates
const delta = 5 * Time.minute

const TwDateRangeText = tw.div<{
  $primary?: boolean
  $bold?: boolean
  $state?: DeadlineState
}>`
  min-w-0
  truncate
  flex-1
  ${p =>
    cn(
      (!p.$state || p.$state === DeadlineState.Planned) && 'text-secondary',
      p.$state === DeadlineState.InProgress && 'text-status-warning',
      p.$state === DeadlineState.Expired && 'text-status-error',
      p.$bold ? 'text-xs-semibold' : 'text-xs-regular',
      p.$primary && '!text-primary'
    )}
`

const TwAddDeadline = tw(IconButtonNew)<{$visible: boolean}>`
  h-full
  w-full
  !justify-start
  hidden
  group-hover/row:flex
  ${p => cn(p.$visible && '!flex')}
`

const DeadlinePopup = observer(
  ({
    hub,
    path,
    reference
  }: {
    hub: Hub
    path: string
    reference: HTMLElement | null
  }) => {
    const [state, store] = useTimelineNewStore()

    const forceUpdate = useForceUpdate()
    useSyncEvent('current', {filter: hub.id}, forceUpdate)
    useEffect(() => GanttDraft.subscribe(forceUpdate), [forceUpdate])

    const editing =
      state.editingPath === path && state.editingColumn === ColumnId.dateRange
    if (!editing || !hub) {
      return null
    }

    const val = Sticker.getDeadline(hub)
    const current = val?.current ?? getDefaultEntry()
    const onEditFormUpdate = (newCurrent?: DeadlineStickerEntry) => {
      if (newCurrent) {
        const history = [...(val?.history ?? []), current]
        const value = getValue(newCurrent, history)
        store.editTaskDeadline(value, hub.id)
      }
    }

    return (
      <Dropdown
        visible
        onVisibleChange={store.stopEditing}
        offset={[0, 6]}
        placement="bottom"
        fallbackPlacements={['top']}
        content={
          <DropdownContent
            className="z-10 min-w-248"
            header={Lang.msg.stickerNames['deadline']}
          >
            <DeadlineEditStatefulForm
              entry={current}
              onUpdate={onEditFormUpdate}
              task={hub}
            />
          </DropdownContent>
        }
        reference={reference}
      />
    )
  }
)

export const DateRange = observer((props: ColumnProps) => {
  if (!props.item.hub) {
    return null
  }

  return <DateRangeBase hub={props.item.hub} {...props} />
})

export const DateRangeBase = observer((props: ColumnProps & {hub: Hub}) => {
  const {
    hub,
    item: {type, path, completed}
  } = props

  const {start, end, state: deadlineState} = useSnappedDeadline(hub.id, path)

  if (type === 'board' && start && end) {
    return <BoardDateRange start={start} end={end} />
  } else if (type === 'column' && start && end) {
    return <ColumnDateRange start={start} end={end} />
  } else if (type === 'task') {
    return (
      <TaskDateRange
        hub={hub}
        path={path}
        start={start}
        deadlineState={deadlineState}
        end={end}
        completed={completed}
      />
    )
  }
  return null
})

export const ColumnDateRange = ({start, end}: {start: number; end: number}) => {
  const text = Time.formatRange(start + delta, end - delta)

  return (
    <TwCell className="cursor-pointer">
      <DateRangeText text={text} bold />
    </TwCell>
  )
}

export const BoardDateRange = ({start, end}: {start: number; end: number}) => {
  const text = Time.formatRange(start + delta, end - delta)

  return (
    <TwCell className="cursor-pointer">
      <DateRangeText text={text} primary bold />
    </TwCell>
  )
}

export const TaskDateRange = observer(
  ({
    hub,
    path,
    start,
    end,
    deadlineState,
    completed
  }: {
    hub: Hub
    path: string
    start: number | undefined
    end: number | undefined
    deadlineState: DeadlineState
    completed?: boolean
  }) => {
    const [reference, setReference] = useState<HTMLElement | null>(null)
    const [state, store] = useTimelineNewStore()
    const config = useTimelineConfig()

    // Update when auto planning changes to redraw lock if required
    useSyncEventUpdateEffect('current', {
      filter: state.board?.id,
      deps: [state.board?.id]
    })

    // Update when dependencies list change to redraw lock if required
    useSyncEventUpdateEffect('current', {filter: hub.id, deps: [hub.id]})

    // Update when dependencies change to redraw lock if required
    const taskDependenciesIds: DataObjectId[] = useMemo(
      () => Object.keys(hub.data.dependencies ?? {}) as DataObjectId[],
      [hub.data.dependencies]
    )
    useSyncEventUpdateEffect('current', {
      filter: taskDependenciesIds,
      deps: [taskDependenciesIds]
    })

    useSyncEventUpdateEffect('current', {filter: hub.id, deps: [hub.id]})

    const editable =
      !config.static &&
      PermissionsChecker.checkEditTaskStickers(hub, 'not-user')

    let content: React.ReactNode
    if (start && end) {
      content = (
        <>
          <DateRangeText
            text={formatDeadline(hub, {
              startDate: start + delta,
              deadline: end - delta,
              timestamp: 0,
              by: globals.currentUserId
            })}
            deadlineState={!completed ? deadlineState : undefined}
          />
          {editable && (
            <IconButtonNew
              icon="IconX"
              className="hidden group-hover/row:block"
              onClick={e => {
                e.stopPropagation()
                store.clearTaskDeadline(hub.id)
              }}
            />
          )}
        </>
      )
    } else if (editable) {
      const isStartBlocked = DeadlineCtl.getBlockedPoints(hub).includes('start')
      const editing =
        state.editingPath === path && state.editingColumn === ColumnId.dateRange

      content = (
        <div className="flex items-center gap-4">
          {isStartBlocked && (
            <BlockedPointText hint={<EmptyBlockedDeadlineHint task={hub} />} />
          )}
          <TwAddDeadline
            $visible={editing}
            forcedState={editing ? 'active' : undefined}
            icon="IconPlus"
            onClick={e => {
              e.stopPropagation()
              store.startEditing(hub.id, path, ColumnId.dateRange)
            }}
          />
        </div>
      )
    }

    return (
      <TwCell
        ref={setReference}
        className={cn('group/icon-button', editable && 'cursor-pointer')}
        onClick={e => {
          if (editable) {
            e.stopPropagation()
            store.startEditing(hub.id, path, ColumnId.dateRange)
          }
        }}
      >
        {content}
        {editable && (
          <DeadlinePopup hub={hub} path={path} reference={reference} />
        )}
      </TwCell>
    )
  }
)

const DateRangeText = React.memo(
  ({
    text,
    deadlineState,
    bold,
    primary
  }: {
    text: React.ReactNode
    deadlineState?: DeadlineState
    bold?: boolean
    primary?: boolean
  }) => {
    const titleRef = useRef<HTMLDivElement>(null)
    const isComplexText = React.isValidElement(text)
    const tooltipVisible = useTooltipVisible(titleRef, [text])
    const {hover, onMouseOver, onMouseOut} = useHover()
    const tooltipEnabled = hover && (tooltipVisible || isComplexText)

    return (
      <Tooltip
        disabled={!tooltipEnabled}
        content={<TwTooltipText>{text}</TwTooltipText>}
        offset={[0, 8]}
        placement="bottom"
      >
        <TwDateRangeText
          $state={deadlineState}
          $bold={bold}
          $primary={primary}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
        >
          {isComplexText ? (
            <div className="flex-1 flex items-center">{text}</div>
          ) : (
            <div ref={titleRef} className="truncate">
              {text}
            </div>
          )}
        </TwDateRangeText>
      </Tooltip>
    )
  }
)
